import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, CheckCircle2, Circle, XCircle, Package } from "lucide-react";
import { http, formatSoles, STATUS_OPTIONS } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const FLOW = ["pendiente_pago", "pago_por_verificar", "pagado", "en_preparacion", "listo_entregar", "entregado"];
const fmt = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString("es-PE", { day: "2-digit", month: "long", year: "numeric" }) : "");

function Timeline({ status }) {
  if (status === "cancelado") {
    return <div className="flex items-center gap-3 p-4 rounded-xl bg-red-50 text-red-700 font-semibold" data-testid="track-cancelled"><XCircle size={20} /> Este pedido fue cancelado</div>;
  }
  const idx = FLOW.indexOf(status);
  return (
    <ol className="space-y-3" data-testid="track-timeline">
      {FLOW.map((s, i) => {
        const done = i <= idx;
        return (
          <li key={s} className="flex items-center gap-3">
            {done ? <CheckCircle2 size={20} className="text-[var(--brand-accent)]" /> : <Circle size={20} className="text-neutral-300" />}
            <span className={`${done ? "font-semibold" : "text-neutral-400"} ${i === idx ? "text-[var(--brand-primary)]" : ""}`}>
              {STATUS_OPTIONS.find((o) => o.value === s)?.label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default function TrackOrder() {
  const [params, setParams] = useSearchParams();
  const [code, setCode] = useState(params.get("codigo") || "");
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function search(c = code) {
    const value = c.trim().toUpperCase();
    if (!value) return;
    setLoading(true);
    setError("");
    setOrder(null);
    try {
      const { data } = await http.get(`/track/${encodeURIComponent(value)}`);
      setOrder(data);
      setParams({ codigo: value }, { replace: true });
    } catch (e) {
      setError(e.response?.data?.detail || "No pudimos consultar el pedido");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { if (params.get("codigo")) search(params.get("codigo")); }, []); // eslint-disable-line

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-12" data-testid="track-page">
      <div className="text-xs uppercase tracking-widest text-[var(--brand-accent)] font-bold">Seguimiento</div>
      <h1 className="font-serif text-4xl sm:text-5xl font-bold mt-2">¿Dónde está mi pedido?</h1>
      <p className="mt-3 text-neutral-600">Escribe el código que recibiste al hacer tu pedido (por ejemplo FR-000012).</p>
      <form onSubmit={(e) => { e.preventDefault(); search(); }} className="mt-8 flex gap-2">
        <Input value={code} onChange={(e) => setCode(e.target.value)} placeholder="FR-000000" className="h-12 bg-white text-lg uppercase" data-testid="track-input" />
        <Button type="submit" disabled={loading} className="btn-primary h-12 rounded-full px-6" data-testid="track-submit">
          <Search size={16} className="mr-2" /> {loading ? "Buscando…" : "Consultar"}
        </Button>
      </form>
      {error && <p className="mt-4 text-[var(--brand-primary)] font-medium" data-testid="track-error">{error}</p>}
      {order && (
        <div className="mt-8 bg-white rounded-3xl border border-black/5 p-6 sm:p-8" data-testid="track-result">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="font-serif text-3xl font-bold text-[var(--brand-primary)]" data-testid="track-code">{order.order_code}</div>
              <div className="text-sm text-neutral-500 mt-1">
                {order.customer_first_name ? `Pedido de ${order.customer_first_name} · ` : ""}realizado el {fmt(order.created_at)}
              </div>
            </div>
            <div className="sm:text-right">
              <div className="text-xs uppercase tracking-widest text-neutral-400">Estado actual</div>
              <div className="font-semibold text-lg" data-testid="track-status">{STATUS_OPTIONS.find((o) => o.value === order.status)?.label}</div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-8">
            <Timeline status={order.status} />
            <div className="space-y-4 text-sm">
              {(order.delivery_date || order.shipping_label) && (
                <div className="p-4 rounded-xl bg-[var(--brand-accent)]/10">
                  {order.shipping_label && <div><b>Entrega:</b> {order.shipping_label}</div>}
                  {order.delivery_date && <div data-testid="track-delivery"><b>Fecha de entrega:</b> {fmt(order.delivery_date)}</div>}
                </div>
              )}
              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-400 font-semibold mb-2 flex items-center gap-1"><Package size={12} /> Productos</div>
                {order.items.map((i, k) => <div key={k}>{i.qty}× {i.name}{i.variant ? ` (${i.variant})` : ""}</div>)}
              </div>
              <div className="border-t pt-3 space-y-1">
                <div className="flex justify-between"><span>Total</span><b>{formatSoles(order.total)}</b></div>
                <div className="flex justify-between"><span>Pagado</span><span>{formatSoles(order.paid)}</span></div>
                <div className="flex justify-between"><span>Saldo pendiente</span><b className={order.balance > 0 ? "text-amber-700" : "text-[var(--brand-accent)]"} data-testid="track-balance">{formatSoles(order.balance)}</b></div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
